import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'

export interface SearchableSelectOption {
  id: string
  label: string
  group?: string
}

interface Props {
  id: string
  label: string
  value: string
  options: SearchableSelectOption[]
  onChange: (id: string) => void
  placeholder?: string
}

function labelFor(options: SearchableSelectOption[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? ''
}

export function SearchableSelect({
  id,
  label,
  value,
  options,
  onChange,
  placeholder = 'Search...',
}: Props) {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(() => labelFor(options, value))
  const [activeIndex, setActiveIndex] = useState(0)

  const selectedLabel = labelFor(options, value)

  useEffect(() => {
    if (!open) setQuery(selectedLabel)
  }, [selectedLabel, open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q || q === selectedLabel.toLowerCase()) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query, selectedLabel])

  const groups = useMemo(() => {
    const map = new Map<string, SearchableSelectOption[]>()
    for (const opt of filtered) {
      const key = opt.group ?? ''
      const list = map.get(key)
      if (list) list.push(opt)
      else map.set(key, [opt])
    }
    return [...map.entries()]
  }, [filtered])

  const indexById = useMemo(() => {
    const map = new Map<string, number>()
    filtered.forEach((opt, i) => map.set(opt.id, i))
    return map
  }, [filtered])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const idx = filtered.findIndex((o) => o.id === value)
    setActiveIndex(idx >= 0 ? idx : 0)
  }, [open, filtered, value])

  const pick = (id: string) => {
    onChange(id)
    setQuery(labelFor(options, id))
    setOpen(false)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        setQuery(selectedLabel)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setActiveIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault()
        pick(filtered[activeIndex].id)
      }
      return
    }
    if (e.key === 'Home' && open) {
      e.preventDefault()
      setActiveIndex(0)
      return
    }
    if (e.key === 'End' && open) {
      e.preventDefault()
      setActiveIndex(Math.max(filtered.length - 1, 0))
    }
  }

  return (
    <div className="searchable-select" ref={rootRef}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[activeIndex] ? `${listId}-opt-${filtered[activeIndex].id}` : undefined
        }
        autoComplete="off"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          setActiveIndex(0)
        }}
        onFocus={(e) => {
          setOpen(true)
          e.currentTarget.select()
        }}
        onClick={(e) => {
          setOpen(true)
          e.currentTarget.select()
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <ul id={listId} className="searchable-select-list" role="listbox" aria-label={label}>
          {filtered.length === 0 ? (
            <li className="searchable-select-empty" role="presentation">
              No matches
            </li>
          ) : (
            groups.map(([group, items]) => (
              <li key={group || 'ungrouped'} role="presentation" className="searchable-select-group">
                {group ? (
                  <div className="searchable-select-group-label" aria-hidden="true">
                    {group}
                  </div>
                ) : null}
                <ul role="group" aria-label={group || label}>
                  {items.map((opt) => {
                    const index = indexById.get(opt.id) ?? 0
                    const selected = opt.id === value
                    const active = index === activeIndex
                    return (
                      <li key={opt.id} role="presentation">
                        <button
                          type="button"
                          id={`${listId}-opt-${opt.id}`}
                          role="option"
                          aria-selected={selected}
                          className={
                            active
                              ? 'searchable-select-option is-active'
                              : 'searchable-select-option'
                          }
                          onMouseEnter={() => setActiveIndex(index)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pick(opt.id)}
                        >
                          {opt.label}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
